import { Link } from 'react-router-dom'
import { Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

const EFFECTIVE_DATE_JA = '2026年6月1日'
const EFFECTIVE_DATE_EN = 'June 1, 2026'

function PublicHeader() {
  const { t, i18n } = useTranslation()
  const toggleLang = () => {
    const next = i18n.language === 'ja' ? 'en' : 'ja'
    void i18n.changeLanguage(next)
    localStorage.setItem('i18n-lang', next)
  }
  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
      <div className="mx-auto max-w-5xl flex h-14 items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleLang} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2">
            {t('legal.switchLang')}
          </button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">{t('legal.login')}</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/signup">{t('legal.startFree')}</Link>
          </Button>
        </div>
      </div>
    </header>
  )
}

function PublicFooter() {
  const { t } = useTranslation()
  return (
    <footer className="border-t py-8 px-6 mt-16">
      <div className="mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </div>
        <nav className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap justify-center">
          <Link to="/" className="hover:text-foreground transition-colors">{t('legal.top')}</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">{t('legal.terms')}</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors">{t('legal.privacy')}</Link>
          <Link to="/legal/commerce" className="hover:text-foreground transition-colors">{t('legal.commerce')}</Link>
        </nav>
        <p className="text-xs text-muted-foreground">© 2026 ScenarioMock</p>
      </div>
    </footer>
  )
}

function PrivacyJa() {
  return (
    <>
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">プライバシーポリシー</h1>
        <p className="text-sm text-muted-foreground">施行日: {EFFECTIVE_DATE_JA}</p>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        ScenarioMock（以下「当社」）は、ユーザーの個人情報の取扱いについて、
        以下のとおりプライバシーポリシーを定めます。
      </p>
      <Separator />
      <Section title="第1条（収集する個人情報）">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">当社は以下の情報を収集します。</p>
        <DataTable rows={[
          ['メールアドレス', 'アカウント登録時'],
          ['パスワード（ハッシュ化）', 'アカウント登録時'],
          ['決済情報', '有料プランへのアップグレード時（Stripe が処理、当社はカード番号を保持しない）'],
          ['Mock へのリクエストログ', 'Mock API 利用時（IPアドレス、ヘッダー、ボディ等）'],
          ['アクセスログ', '本サービスへのアクセス時（IPアドレス、ブラウザ情報等）'],
        ]} />
      </Section>
      <Section title="第2条（個人情報の利用目的）">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">収集した個人情報は以下の目的に利用します。</p>
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>本サービスの提供・運営・改善</li>
          <li>ユーザー認証およびアカウント管理</li>
          <li>課金・請求処理</li>
          <li>サービスに関する重要なお知らせの送信</li>
          <li>不正利用の検知・防止</li>
          <li>お問い合わせへの対応</li>
        </ol>
      </Section>
      <Section title="第3条（第三者への提供）">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">
          当社は、以下の場合を除き、ユーザーの個人情報を第三者に提供しません。
        </p>
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>ユーザーの同意がある場合</li>
          <li>法令に基づく開示要求がある場合</li>
          <li>サービス提供のために必要な業務委託先（下記参照）に提供する場合</li>
        </ol>
        <p className="text-sm leading-relaxed text-muted-foreground mt-3 mb-2 font-medium">利用する主な外部サービス:</p>
        <DataTable rows={[
          ['Stripe, Inc.', '課金・決済処理', 'https://stripe.com/privacy'],
          ['Neon, Inc.', 'データベースホスティング', 'https://neon.tech/privacy'],
          ['Upstash, Inc.', 'Redis ホスティング', 'https://upstash.com/trust/privacy.pdf'],
          ['Fly.io, Inc.', 'アプリケーションホスティング', 'https://fly.io/legal/privacy-policy'],
        ]} />
      </Section>
      <Section title="第4条（Cookie の利用）">
        <p className="text-sm leading-relaxed text-muted-foreground">
          当社は、ユーザーの利便性向上のために Cookie を使用します。
          Cookie にはセッション管理のための情報のみを保存し、個人を特定できる情報は含みません。
          ブラウザの設定により Cookie を無効にすることができますが、
          その場合は一部の機能が利用できなくなる場合があります。
        </p>
      </Section>
      <Section title="第5条（データ保持期間）">
        <DataTable rows={[
          ['アカウント情報', '退会後30日間'],
          ['Mock リクエストログ', '記録から30日間（自動削除）'],
          ['決済履歴', '法令に従い7年間（Stripe が管理）'],
        ]} />
      </Section>
      <Section title="第6条（安全管理措置）">
        <p className="text-sm leading-relaxed text-muted-foreground">
          当社は、個人情報の漏洩・滅失・毀損の防止のために適切な安全管理措置を講じます。
          パスワードは Argon2 によりハッシュ化して保存し、通信は TLS により暗号化します。
          ただし、インターネット上の完全な安全性を保証することはできません。
        </p>
      </Section>
      <Section title="第7条（プライバシーポリシーの変更）">
        <p className="text-sm leading-relaxed text-muted-foreground">
          当社は必要に応じてプライバシーポリシーを変更することがあります。
          重要な変更の場合は、本サービス上の表示またはメールにより事前に通知します。
        </p>
      </Section>
      <Section title="第8条（お問い合わせ）">
        <p className="text-sm leading-relaxed text-muted-foreground">
          個人情報の取扱いに関するお問い合わせ、開示・訂正・削除の請求は下記までご連絡ください。
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          メール:{' '}
          <a href="mailto:support@scenariomock.com" className="text-primary underline underline-offset-4">
            support@scenariomock.com
          </a>
        </p>
      </Section>
      <Separator />
      <p className="text-xs text-muted-foreground">本ポリシーは日本法に準拠します。</p>
    </>
  )
}

function PrivacyEn() {
  return (
    <>
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">Effective date: {EFFECTIVE_DATE_EN}</p>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        ScenarioMock ("we", "us", or "our") sets out the following Privacy Policy
        regarding the handling of Users' personal information.
      </p>
      <Separator />
      <Section title="Article 1 (Information We Collect)">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">We collect the following information:</p>
        <DataTable rows={[
          ['Email address', 'At account registration'],
          ['Password (hashed)', 'At account registration'],
          ['Payment information', 'When upgrading to a paid plan (processed by Stripe; we do not store card numbers)'],
          ['Mock request logs', 'When using the Mock API (IP address, headers, body, etc.)'],
          ['Access logs', 'When accessing the Service (IP address, browser information, etc.)'],
        ]} />
      </Section>
      <Section title="Article 2 (Purpose of Use)">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">We use collected personal information for the following purposes:</p>
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>Providing, operating, and improving the Service</li>
          <li>User authentication and account management</li>
          <li>Billing and payment processing</li>
          <li>Sending important notices related to the Service</li>
          <li>Detecting and preventing fraudulent use</li>
          <li>Responding to inquiries</li>
        </ol>
      </Section>
      <Section title="Article 3 (Third-Party Disclosure)">
        <p className="text-sm leading-relaxed text-muted-foreground mb-3">
          We do not provide Users' personal information to third parties except in the following cases:
        </p>
        <ol className="list-decimal list-inside space-y-1 text-sm leading-relaxed text-muted-foreground">
          <li>When the User has given consent</li>
          <li>When required by law</li>
          <li>When provided to service providers necessary for providing the Service (see below)</li>
        </ol>
        <p className="text-sm leading-relaxed text-muted-foreground mt-3 mb-2 font-medium">Key third-party services we use:</p>
        <DataTable rows={[
          ['Stripe, Inc.', 'Billing and payment processing', 'https://stripe.com/privacy'],
          ['Neon, Inc.', 'Database hosting', 'https://neon.tech/privacy'],
          ['Upstash, Inc.', 'Redis hosting', 'https://upstash.com/trust/privacy.pdf'],
          ['Fly.io, Inc.', 'Application hosting', 'https://fly.io/legal/privacy-policy'],
        ]} />
      </Section>
      <Section title="Article 4 (Cookies)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          We use cookies to improve your experience. Cookies only store session management information
          and do not contain personally identifiable information. You may disable cookies in your browser settings,
          but some features may not function properly if you do so.
        </p>
      </Section>
      <Section title="Article 5 (Data Retention)">
        <DataTable rows={[
          ['Account information', '30 days after cancellation'],
          ['Mock request logs', '30 days from recording (automatically deleted)'],
          ['Payment history', '7 years as required by law (managed by Stripe)'],
        ]} />
      </Section>
      <Section title="Article 6 (Security Measures)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          We take appropriate security measures to prevent leakage, loss, or damage of personal information.
          Passwords are stored as Argon2 hashes, and communications are encrypted via TLS.
          However, we cannot guarantee complete security over the internet.
        </p>
      </Section>
      <Section title="Article 7 (Policy Changes)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          We may update this Privacy Policy as necessary. Significant changes will be notified
          via the Service or by email in advance.
        </p>
      </Section>
      <Section title="Article 8 (Contact)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          For inquiries, disclosure requests, corrections, or deletion requests regarding personal information, please contact us at:
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Email:{' '}
          <a href="mailto:support@scenariomock.com" className="text-primary underline underline-offset-4">
            support@scenariomock.com
          </a>
        </p>
      </Section>
      <Separator />
      <p className="text-xs text-muted-foreground">This policy is governed by the laws of Japan.</p>
    </>
  )
}

export function PrivacyPage() {
  const { i18n } = useTranslation()
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-6 py-16 space-y-10">
          {i18n.language === 'ja' ? <PrivacyJa /> : <PrivacyEn />}
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {children}
    </section>
  )
}

function DataTable({ rows }: { rows: string[][] }) {
  return (
    <div className="rounded-md border overflow-hidden">
      <table className="w-full text-sm">
        <tbody className="divide-y">
          {rows.map((row, i) => (
            <tr key={i} className="bg-background">
              {row.map((cell, j) => (
                <td key={j} className={`px-4 py-2.5 align-top ${j === 0 ? 'font-medium text-foreground w-40 shrink-0' : 'text-muted-foreground'}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
